// index.tsx
// SurgeonSearch component for searching and subscribing to new surgeons by name or NPI.
// Provides a sidebar UI for searching, displaying results, and subscribing, with feedback and error handling.

import { useState, ChangeEvent } from "react";
import { styled } from "@stitches/react";
import { Button, Heading, Input, Toast } from "@Forge/dist";
import { SurgeonSearch as SurgeonSearchType } from "@/types/surgeons";
import { PlusIcon } from "@/components/Icons";
import { useSurgeons } from "@/hooks/use-surgeons";
import Loader from "@/components/Loader";

/**
 * Sidebar container for the surgeon search UI.
 */
const SearchSidebar = styled("div", {
  width: "300px",
  padding: "12px",
  backgroundColor: "#f3f3f3",
  display: "flex",
  flexDirection: "column",
});

/**
 * Container for search results.
 */
const SearchResults = styled("div", {
  display: "flex",
  flexDirection: "column",
  overflowY: "scroll",
  flex: 1,
  marginTop: "16px",
});

/**
 * Wrapper for each surgeon search result.
 */
const SurgeonResultWrapper = styled("div", {
  display: "flex",
  padding: "16px 0",
  flexDirection: "column",
  borderBottom: "1px solid #b6b7b7",
});

/**
 * Header for a surgeon search result.
 */
const SurgeonResultHeader = styled("p", {
  color: "#000000 !important",
  margin: "0 !important",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingRight: "16px",
});

/**
 * Text for surgeon specialty and NPI.
 */
const SurgeonResultText = styled("p", {
  margin: "0 !important",
});

/**
 * Message for no search results.
 */
const SurgeonSearchNoResults = styled("p", {
  padding: "16px 0",
  display: "flex",
  alignItems: "center",
});

/**
 * SurgeonSearchResult Component
 * Displays a single surgeon search result with subscribe button and feedback.
 * Handles subscribe logic and feedback.
 * @param surgeon - The surgeon search result data
 * @param refetch - Callback to refetch search results
 * @param setToastData - Callback to set toast feedback
 */
const SurgeonSearchResult = ({
  surgeon,
  refetch,
  setToastData,
}: {
  surgeon: SurgeonSearchType;
  refetch: () => Promise<void>;
  setToastData: (data: {
    message: string;
    header: string;
    type: "success" | "attention" | "info";
  }) => void;
}) => {
  const {
    post: {
      subscribe,
      error: subscribeError,
      isError: isSubscribeError,
      isPending: isSubscribing,
    },
  } = useSurgeons();

  const handleSurgeonSubscribe = (surgeon: SurgeonSearchType) => {
    subscribe(surgeon, () => {
      setToastData({
        message: `${surgeon.displayName} has been added to your view.`,
        header: `You've added ${surgeon.displayName}`,
        type: "success",
      });
      return refetch();
    });
  };

  return (
    <SurgeonResultWrapper>
      <Loader
        displayText={`Adding ${surgeon.displayName} to your surgeon list.`}
        isLoading={isSubscribing}
        isError={isSubscribeError}
        errorReason={subscribeError?.message}
      >
        <SurgeonResultHeader>
          {surgeon.displayName}
          <Button
            onClick={() => handleSurgeonSubscribe(surgeon)}
            variant="tertiary"
          >
            <PlusIcon />
          </Button>
        </SurgeonResultHeader>
        <SurgeonResultText>{surgeon.specialty}</SurgeonResultText>
        <SurgeonResultText>NPI: {surgeon.npi}</SurgeonResultText>
      </Loader>
    </SurgeonResultWrapper>
  );
};

/**
 * SurgeonSearch Component
 * Provides a sidebar UI for searching and subscribing to surgeons by name or NPI.
 * Handles search, result display, subscribe, and feedback states.
 */
const SurgeonSearch = () => {
  const {
    search: { data, isPending, isError, error, search },
  } = useSurgeons();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [firstSearch, setFirstSearch] = useState<boolean>(true);
  const [toastData, setToastData] = useState<{
    message: string;
    header: string;
    type: "success" | "attention" | "info";
  } | null>(null);

  /**
   * Handles the search form submit event.
   */
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchTerm.length > 3) {
      search(searchTerm);
      setFirstSearch(false);
    }
  };

  /**
   * Generates the list of search results or a no-results message.
   */
  const generateResults = () => {
    if (data?.length) {
      return data?.map((surgeon) => (
        <SurgeonSearchResult
          surgeon={surgeon}
          key={surgeon.npi}
          refetch={() => new Promise(() => search(searchTerm))}
          setToastData={setToastData}
        />
      ));
    }

    if (searchTerm && !firstSearch && !isPending) {
      return (
        <SurgeonSearchNoResults>
          No results found for the provided search term. Please try a new
          search.
        </SurgeonSearchNoResults>
      );
    }

    return null;
  };

  return (
    <SearchSidebar>
      <Heading
        text="Add A New Surgeon"
        variant="subsection"
        style={{ color: "#5d5e5e" }}
      />
      <form onSubmit={handleSearch}>
        <Input
          placeholder="Search by Name or NPI"
          type="text"
          name="search"
          icon="Search"
          clearable
          minLength={4}
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          data-testid="test-search-surgeons"
        />
      </form>
      <SearchResults>
        <Loader
          displayText="Searching..."
          isLoading={isPending}
          isError={isError}
          errorReason={error?.message}
        >
          {generateResults()}
        </Loader>
      </SearchResults>
      {toastData && (
        <Toast
          id="surgeon-toast"
          headerText={toastData?.header}
          alertType={toastData?.type}
          onDismiss={() => setToastData(null)}
        >
          {toastData?.message}
        </Toast>
      )}
    </SearchSidebar>
  );
};

export default SurgeonSearch;
